import { useCurrency } from '../../../hooks/useCurrency';
import { getDisplayPrice, getSecondaryPrice } from '../../../lib/currency';

interface Product {
  id: string;
  name: string;
  price: number;
  oldPrice?: number;
  discount: string;
  image: string;
  type: string;
  description: string;
  badge?: string;
  badgeColor?: string;
  salesCount?: number;
  bestOption?: boolean;
  priceNote?: string;
  multiplyBy?: number;
  licenseText?: string;
}

interface StepSelectorProps {
  stepNumber: number;
  title: string;
  subtitle: string;
  icon: string;
  iconColor: string;
  products: Product[];
  selectedId: string | null;
  onSelect: (product: Product) => void;
  variant?: 'compact' | 'card';
}

/**
 * Helper to safely get price information.
 * Falls back to a default representation if the library throws.
 */
function safeGetDisplayPrice(
  price: number,
  oldPrice: number | undefined,
  currency: string,
) {
  try {
    return getDisplayPrice(price, oldPrice, currency);
  } catch (error) {
    console.error('Error calculating display price:', error);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(price);
    return { main: formatted, oldMain: oldPrice ? formatted : undefined };
  }
}

/**
 * Helper to safely get secondary price information.
 */
function safeGetSecondaryPrice(
  price: number,
  oldPrice: number | undefined,
  currency: string,
) {
  try {
    return getSecondaryPrice(price, oldPrice, currency);
  } catch (error) {
    console.error('Error calculating secondary price:', error);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(price);
    return { current: formatted };
  }
}

export default function StepSelector({
  stepNumber,
  title,
  subtitle,
  icon,
  iconColor,
  products,
  selectedId,
  onSelect,
  variant = 'compact',
}: StepSelectorProps) {
  const { currency } = useCurrency();

  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-10 h-10 rounded-full ${iconColor} text-white flex items-center justify-center font-bold text-lg flex-shrink-0`}
        >
          {stepNumber}
        </div>
        <div>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
            <i className={`${icon} text-lg`}></i>
            {title}
          </h3>
          <p className="text-xs sm:text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>

      {variant === 'card' ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-product-shop>
          {products.map((product) => {
            const isSelected = selectedId === product.id;
            const display = safeGetDisplayPrice(product.price, product.oldPrice, currency);
            const secondary = safeGetSecondaryPrice(product.price, product.oldPrice, currency);

            return (
              <button
                key={product.id}
                onClick={() => onSelect(product)}
                className={`relative text-left bg-white border-2 rounded-lg overflow-hidden transition-all duration-300 cursor-pointer group ${
                  isSelected
                    ? 'border-teal-500 shadow-lg shadow-teal-100 ring-2 ring-teal-100'
                    : product.bestOption
                    ? 'border-emerald-500 ring-2 ring-emerald-100 hover:shadow-lg'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
              >
                {isSelected && (
                  <div className="absolute top-2 right-2 z-20 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                )}
                {product.bestOption && (
                  <div className="bg-emerald-500 text-white text-center py-1 sm:py-1.5 text-[10px] sm:text-sm font-bold tracking-wide">
                    <i className="ri-star-fill mr-1"></i>
                    <span className="hidden sm:inline">Mejor Opción</span>
                    <span className="sm:hidden">Mejor</span>
                  </div>
                )}
                <div className="relative">
                  {product.badge && !product.bestOption && (
                    <span
                      className={`absolute top-2 sm:top-3 left-2 sm:left-3 z-10 ${
                        product.badgeColor || 'bg-amber-500'
                      } text-white text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded whitespace-nowrap`}
                    >
                      {product.badge}
                    </span>
                  )}
                  {product.discount && (
                    <span className="absolute top-2 sm:top-3 right-2 sm:right-3 z-10 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded whitespace-nowrap">
                      {product.discount}
                    </span>
                  )}
                  <div className="w-full h-28 sm:h-36 md:h-44 bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                    />
                  </div>
                </div>
                <div className="p-3 sm:p-4">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2 h-8 sm:h-10">
                    {product.name}
                  </h4>
                  <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-sm"></i>
                    ))}
                    <span className="text-[9px] sm:text-xs text-gray-500 ml-0.5">(5.0)</span>
                  </div>
                  {product.salesCount && (
                    <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                      <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                      <span className="text-[10px] sm:text-xs text-gray-500">
                        {product.salesCount.toLocaleString('es-CO')} vendidos
                      </span>
                    </div>
                  )}
                  <p className="text-[10px] sm:text-xs text-gray-500 mb-1.5 line-clamp-1">{product.description}</p>
                  
                  {/* Licencia de por vida para productos Office */}
                  {stepNumber === 1 && (
                    <div className="flex items-center gap-1 mb-1.5">
                      <i className="ri-shield-check-line text-[10px] sm:text-xs text-blue-600"></i>
                      <span className="text-[10px] sm:text-xs text-blue-600 font-medium">
                        Licencia de por vida
                      </span>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                    <span className="text-sm sm:text-lg font-bold text-gray-900">
                      {display.main}
                      {product.priceNote && <span className="text-[10px] sm:text-xs font-normal text-gray-500">{product.priceNote}</span>}
                    </span>
                    {display.oldMain && (
                      <span className="text-[10px] sm:text-sm text-gray-500 line-through">{display.oldMain}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">
                      &asymp; {secondary.current}
                      {product.priceNote && <span>{product.priceNote}</span>}
                    </p>
                  </div>
                  {product.multiplyBy && product.multiplyBy > 1 && product.licenseText && (
                    <p className="text-[10px] sm:text-xs text-emerald-600 font-medium mt-1 hidden sm:block">
                      <i className="ri-group-line mr-1"></i>
                      Incluye {product.licenseText}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {products.map((product) => {
            const isSelected = selectedId === product.id;
            const display = safeGetDisplayPrice(product.price, product.oldPrice, currency);
            const secondary = safeGetSecondaryPrice(product.price, product.oldPrice, currency);

            return (
              <button
                key={product.id}
                onClick={() => onSelect(product)}
                className={`relative text-left rounded-xl border-2 transition-all duration-300 cursor-pointer group overflow-hidden ${
                  isSelected
                    ? 'border-teal-500 bg-teal-50/60 shadow-lg shadow-teal-100'
                    : product.bestOption
                    ? 'border-emerald-500 bg-emerald-50/30 shadow-md shadow-emerald-100 ring-2 ring-emerald-200/60 hover:shadow-lg hover:shadow-emerald-200'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                }`}
              >
                {/* Banner "Mejor Opción" para productos destacados */}
                {product.bestOption && (
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-center py-1.5 text-[10px] sm:text-xs font-bold tracking-wide flex items-center justify-center gap-1.5">
                    <i className="ri-star-fill text-yellow-200 text-xs"></i>
                    <span>Mejor Opción</span>
                    <i className="ri-star-fill text-yellow-200 text-xs"></i>
                  </div>
                )}

                {/* Solo productos Office (stepNumber === 1) tienen badges en fila */}
                {stepNumber === 1 && (product.badge || product.discount) && (
                  <div className="flex items-center gap-1.5 px-3 pt-2.5 sm:px-4 sm:pt-3 flex-wrap">
                    {product.badge && !product.bestOption && (
                      <span
                        className={`${
                          product.badgeColor || 'bg-amber-500'
                        } text-white text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap shadow-sm`}
                      >
                        {product.badge}
                      </span>
                    )}
                    {product.discount && (
                      <span className="bg-red-500 text-white text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                        {product.discount}
                      </span>
                    )}
                  </div>
                )}

                {/* Check de selección */}
                {isSelected && (
                  <div className={`absolute ${product.bestOption ? 'top-10' : stepNumber === 1 ? 'top-2.5' : 'top-3'} right-2.5 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center z-10`}>
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                )}

                {/* Contenido del producto */}
                <div className={`flex items-center gap-3 p-3 sm:p-4 ${stepNumber === 1 && !product.bestOption ? 'pt-2 sm:pt-2.5' : ''}`}>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-gray-50 rounded-lg flex items-center justify-center p-1.5">
                    <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0 pr-6">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-900 line-clamp-2 leading-tight mb-1">
                      {product.name}
                    </h4>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-1.5 line-clamp-1">{product.description}</p>
                    
                    {/* Licencia de por vida para productos Office en vista compacta */}
                    {stepNumber === 1 && (
                      <div className="flex items-center gap-1 mb-1.5">
                        <i className="ri-shield-check-line text-[10px] sm:text-xs text-blue-600"></i>
                        <span className="text-[10px] sm:text-xs text-blue-600 font-medium">
                          Licencia de por vida
                        </span>
                      </div>
                    )}
                    
                    <div className="flex flex-col gap-0">
                      <span className="text-sm sm:text-base font-bold text-gray-900">{display.main}</span>
                      {display.oldMain && (
                        <span className="text-[10px] sm:text-xs text-gray-400 line-through">{display.oldMain}</span>
                      )}
                    </div>
                    <p className="text-[10px] text-emerald-600 font-medium mt-0.5">
                      &asymp; {secondary.current}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
